<template>
	<div class="alfa_targets_limits text_left">
		<!-- Total investment volume -->
		<h4 class="font_bold">
			{{ $t("investor.exchange.modal.alfa.targetsAndLimits.totalInvestmentVolume.title") }}
		</h4>
		<div class="flex flex_items_center margin_top" style="max-width: 300px;">
			<md-field :class="[{ 'md-error': errors.has('totalInvestmentVolume') }]">
				<label>{{ $t("common.alfa.totalInvestmentVolume") }}</label>
				<MoneyAmountInput class="text_right"
								  v-model="data.totalInvestmentVolume"
								  data-vv-name="totalInvestmentVolume"
								  v-validate="modelValidations.totalInvestmentVolume"
								  :required="modelValidations.totalInvestmentVolume"
				/>
				<span v-if="errors.has('totalInvestmentVolume')" class="validation-error margin_l">
					{{ errors.first("totalInvestmentVolume") }}
				</span>
				<span class="flex flex_items_center margin_l">
					{{ currency }}
				</span>
			</md-field>
		</div>
		<span class="secondary_color">
			{{ $t("investor.exchange.modal.alfa.targetsAndLimits.usedInvestmentVolume", { amount: usedInvestmentVolumeFormatted, currency: currency }) }}
		</span>

		<!-- TCI preference -->
		<!-- TCI IS DISABLED FOR NOW
		<h4 class="font_bold margin_top_l margin_bottom">
			{{ $t("investor.exchange.modal.alfa.targetsAndLimits.tciPreference.title") }}
		</h4>

		<div v-if="enumDef.creditInsurancePolicyHolderStatus.hasRequestedAccess(tciConfirmationStatus)"
			 class="base_padding default_radius alfa_light_grey_bg"
		>
			{{ $t("investor.exchange.modal.alfa.targetsAndLimits.tciPreference.accessRequested") }}
		</div>
		<ActivateTciButton v-else-if="!enumDef.creditInsurancePolicyHolderStatus.canBuyTci(tciConfirmationStatus)"/>

		<div class="flex flex_column">
			<md-radio :class="{ 'md-error': errors.first('tciPreference') }"
					  v-model="data.tciPreference"
					  @change="onTciPreferenceChanged"
					  :value="enumDef.alfaBuyPreference.Only"
					  v-validate="modelValidations.tciPreference"
					  data-vv-name="tciPreference"
					  :required="modelValidations.tciPreference"
					  :disabled="!enumDef.creditInsurancePolicyHolderStatus.canBuyTci(tciConfirmationStatus)"
			>
				{{ $t("common.alfa.tciPreference.only") }}
			</md-radio>
			<md-radio :class="{ 'md-error': errors.first('tciPreference') }"
					  v-model="data.tciPreference"
					  @change="onTciPreferenceChanged"
					  :value="enumDef.alfaBuyPreference.Preferred"
					  v-validate="modelValidations.tciPreference"
					  data-vv-name="tciPreference"
					  :required="modelValidations.tciPreference"
					  :disabled="!enumDef.creditInsurancePolicyHolderStatus.canBuyTci(tciConfirmationStatus)"
			>
				{{ $t("common.alfa.tciPreference.preferred") }}
			</md-radio>
			<md-radio :class="{ 'md-error': errors.first('tciPreference') }"
					  v-model="data.tciPreference"
					  @change="onTciPreferenceChanged"
					  :value="enumDef.alfaBuyPreference.Never"
					  v-validate="modelValidations.tciPreference"
					  data-vv-name="tciPreference"
					  :required="modelValidations.tciPreference"
			>
				{{ $t("common.alfa.tciPreference.never") }}
			</md-radio>
		</div>
		-->

		<!-- Target return -->
		<h4 class="font_bold margin_top_l margin_bottom">
			{{ $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.title") }}
		</h4>
		<p>
			{{ $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.description") }}
		</p>

		<!-- Target return without TCI preference set -->
		<div v-if="data.tciPreference === null" class="base_padding default_radius alfa_light_grey_bg">
			<span>{{ $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.tciPreferenceRequired")}}</span>
		</div>
		<template v-else>
			<!-- Target return with TCI -->
			<div v-if="data.tciPreference !== enumDef.alfaBuyPreference.Never" class="base_padding default_radius light_bg_color roi_block">
				<h4 class="font_bold secondary_color margin_bottom">
					{{ data.tciPreference === enumDef.alfaBuyPreference.Preferred
						? $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withTci.title.tciPreferencePreferred")
						: $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withTci.title")
					}}
				</h4>
				<p>
					{{ $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withTci.description")}}
				</p>
				<div style="max-width: 250px;">
					<md-field :class="[{ 'md-error': errors.has('netTargetReturnRatePerAnnumWithTci') }]">
						<label>{{ $t("common.alfa.netTargetReturnRatePerAnnumWithTci") }}</label>
						<MoneyAmountInput class="text_right"
										  v-model="data.netTargetReturnRatePerAnnumWithTci"
										  :as-percentage-rate="true"
										  data-vv-name="netTargetReturnRatePerAnnumWithTci"
										  v-validate="modelValidations.netTargetReturnRatePerAnnumWithTci"
										  :required="modelValidations.netTargetReturnRatePerAnnumWithTci"
						/>
						<!-- Do not display validation error because of size constraints -->
						<span class="md-input-suffix">%</span>
					</md-field>
				</div>
				<div class="flex">
					<md-checkbox v-if="currency" v-model="data.targetReturnRateAddReferenceInterestRateWithTci">
						{{ $t("common.alfa.targetReturnRateAddReferenceInterestRate." + currency) }}
						<el-tooltip :open-delay="300" placement="top"
									:content="$t('common.alfa.targetReturnRateAddReferenceInterestRate.' + currency + '.tooltip')">
							<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
						</el-tooltip>
					</md-checkbox>
					<md-checkbox v-model="data.targetReturnRateAddFees" disabled>
						{{ $t("common.alfa.targetReturnRateAddFees") }}
					</md-checkbox>
					<div class="flex">
						<md-checkbox class="flex_1" v-model="data.targetReturnRateAddInsurancePremium" disabled>
							{{ $t("common.alfa.targetReturnRateAddInsurancePremium") }}
							<el-tooltip v-if="$t('common.alfa.targetReturnRateAddInsurancePremium.tooltip')" :open-delay="300" placement="top"
										:content="$t('common.alfa.targetReturnRateAddInsurancePremium.tooltip')">
								<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
							</el-tooltip>
						</md-checkbox>
					</div>
				</div>
				<el-tooltip :open-delay="300" placement="bottom" class="roi_block_tooltip">
					<span slot="content" v-html="$t('investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withTci.tooltip')"></span>
					<i class="fa fa-info-circle secondary_color" aria-hidden="true"/>
				</el-tooltip>
			</div>
			<!-- Divider -->
			<div v-if="data.tciPreference === enumDef.alfaBuyPreference.Preferred" class="margin_vertical"></div>
			<!-- Target return without TCI -->
			<div v-if="data.tciPreference !== enumDef.alfaBuyPreference.Only" class="base_padding default_radius light_bg_color roi_block">
				<h4 class="font_bold secondary_color margin_bottom">
					{{ data.tciPreference === enumDef.alfaBuyPreference.Preferred
						? $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withoutTci.title.tciPreferencePreferred")
						: $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withoutTci.title")
					}}
				</h4>
				<p>
					{{ $t("investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withoutTci.description")}}
				</p>
				<div class="flex">
					<!-- Low Risk -->
					<md-field :class="[{ 'md-error': errors.has('netTargetReturnRatePerAnnumRiskLow') }]" class="margin_r">
						<label>{{ $t("common.riskCategory.low") }}</label>
						<MoneyAmountInput class="text_right"
										  v-model="data.netTargetReturnRatePerAnnumRiskLow"
										  :as-percentage-rate="true"
										  data-vv-name="netTargetReturnRatePerAnnumRiskLow"
										  v-validate="modelValidations.netTargetReturnRatePerAnnumRiskLow"
										  :required="modelValidations.netTargetReturnRatePerAnnumRiskLow"
						/>
						<!-- Do not display validation error because of size constraints -->
						<span class="md-input-suffix">%</span>
					</md-field>
					<!-- Medium Risk -->
					<md-field :class="[{ 'md-error': errors.has('netTargetReturnRatePerAnnumRiskMedium') }]" class="margin_r">
						<label>{{ $t("common.riskCategory.medium") }}</label>
						<MoneyAmountInput class="text_right"
										  v-model="data.netTargetReturnRatePerAnnumRiskMedium"
										  :as-percentage-rate="true"
										  data-vv-name="netTargetReturnRatePerAnnumRiskMedium"
										  v-validate="modelValidations.netTargetReturnRatePerAnnumRiskMedium"
										  :required="modelValidations.netTargetReturnRatePerAnnumRiskMedium"
						/>
						<!-- Do not display validation error because of size constraints -->
						<span class="md-input-suffix">%</span>
					</md-field>
					<!-- High Risk -->
					<md-field :class="[{ 'md-error': errors.has('netTargetReturnRatePerAnnumRiskHigh') }]" class="margin_r">
						<label>{{ $t("common.riskCategory.high") }}</label>
						<MoneyAmountInput class="text_right"
										  v-model="data.netTargetReturnRatePerAnnumRiskHigh"
										  :as-percentage-rate="true"
										  data-vv-name="netTargetReturnRatePerAnnumRiskHigh"
										  v-validate="modelValidations.netTargetReturnRatePerAnnumRiskHigh"
										  :required="modelValidations.netTargetReturnRatePerAnnumRiskHigh"
						/>
						<!-- Do not display validation error because of size constraints -->
						<span class="md-input-suffix">%</span>
					</md-field>
					<!-- Unknown Risk -->
					<md-field :class="[{ 'md-error': errors.has('netTargetReturnRatePerAnnumRiskUnrated') }]">
						<label>{{ $t("common.riskCategory.unknown") }}</label>
						<MoneyAmountInput class="text_right"
										  v-model="data.netTargetReturnRatePerAnnumRiskUnrated"
										  :as-percentage-rate="true"
										  data-vv-name="netTargetReturnRatePerAnnumRiskUnrated"
										  v-validate="modelValidations.netTargetReturnRatePerAnnumRiskUnrated"
										  :required="modelValidations.netTargetReturnRatePerAnnumRiskUnrated"
						/>
						<!-- Do not display validation error because of size constraints -->
						<span class="md-input-suffix">%</span>
					</md-field>
				</div>
				<div class="flex">
					<md-checkbox v-if="currency" v-model="data.targetReturnRateAddReferenceInterestRateWithoutTci">
						{{ $t("common.alfa.targetReturnRateAddReferenceInterestRate." + currency) }}
						<el-tooltip :open-delay="300" placement="top"
									:content="$t('common.alfa.targetReturnRateAddReferenceInterestRate.' + currency + '.tooltip')">
							<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
						</el-tooltip>
					</md-checkbox>
					<md-checkbox v-model="data.targetReturnRateAddFees" disabled>
						{{ $t("common.alfa.targetReturnRateAddFees") }}
					</md-checkbox>
				</div>
				<el-tooltip :open-delay="300" placement="bottom" class="roi_block_tooltip">
					<span slot="content" v-html="$t('investor.exchange.modal.alfa.targetsAndLimits.targetReturn.withoutTci.tooltip')"></span>
					<i class="fa fa-info-circle secondary_color" aria-hidden="true"/>
				</el-tooltip>
			</div>
		</template>

		<!-- Retention preference -->
		<h4 class="font_bold margin_top_l margin_bottom">
			{{ $t("investor.exchange.modal.alfa.targetsAndLimits.retentionPreference.title", { retentionRateInPercent: retentionRateInPercentFormatted }) }}
		</h4>
		<div class="flex flex_column">
			<md-radio :class="{ 'md-error': errors.first('retentionPreference') }"
					  v-model="data.retentionPreference"
					  :value="enumDef.alfaBuyPreference.Only"
					  v-validate="modelValidations.retentionPreference"
					  data-vv-name="retentionPreference"
					  :required="modelValidations.retentionPreference"
			>
				{{ $t("common.alfa.retentionPreference.only") }}
			</md-radio>
			<md-radio :class="{ 'md-error': errors.first('retentionPreference') }"
					  v-model="data.retentionPreference"
					  :value="enumDef.alfaBuyPreference.Preferred"
					  v-validate="modelValidations.retentionPreference"
					  data-vv-name="retentionPreference"
					  :required="modelValidations.retentionPreference"
			>
				{{ $t("common.alfa.retentionPreference.preferred") }}
			</md-radio>
			<md-radio :class="{ 'md-error': errors.first('retentionPreference') }"
					  v-model="data.retentionPreference"
					  :value="enumDef.alfaBuyPreference.Never"
					  v-validate="modelValidations.retentionPreference"
					  data-vv-name="retentionPreference"
					  :required="modelValidations.retentionPreference"
			>
				{{ $t("common.alfa.retentionPreference.never") }}
			</md-radio>
		</div>
	</div>
</template>

<script>
import {enumDef} from "@/common/enumDefs";
import MoneyAmountInput from "@/components/templates/common/MoneyAmountInput";
import {validateForm} from "@/validation/validation";
import {formatAsMoneyAmount, formatAsPercentage} from "@/common/format";
import ActivateTciButton from "@/components/templates/investor/exchange/receivables/ActivateTciButton.vue";

export default {
	components: { ActivateTciButton, MoneyAmountInput },
	props: {
		data: { type: Object, default: {} },
		currency: { type: String, default: "" },
		tciConfirmationStatus: { type: Number, default: enumDef.creditInsurancePolicyHolderStatus.NONE }
	},
	data() {
		return {
			modelValidations: {
				totalInvestmentVolume: {
					required: true
				},
				tciPreference: {
					required: true
				},
				retentionPreference: {
					required: true
				},
				netTargetReturnRatePerAnnumRiskLow: {
					required: true
				},
				netTargetReturnRatePerAnnumRiskMedium: {
					required: true
				},
				netTargetReturnRatePerAnnumRiskHigh: {
					required: true
				},
				netTargetReturnRatePerAnnumRiskUnrated: {
					required: true
				},
				netTargetReturnRatePerAnnumWithTci: {
					required: true
				}
			},
			enumDef: enumDef
		};
	},
	computed: {
		retentionRateInPercentFormatted() {
			return formatAsPercentage(this.$store.getters.auctionConfig.purchaseRetentionRate, { decimals: 0 });
		},
		usedInvestmentVolumeFormatted() {
			return formatAsMoneyAmount(this.data.usedInvestmentVolume);
		}
	},
	methods: {
		onTciPreferenceChanged() {
			if (this.data.tciPreference === enumDef.alfaBuyPreference.Only) {
				this.data.netTargetReturnRatePerAnnumRiskLow = null;
				this.data.netTargetReturnRatePerAnnumRiskMedium = null;
				this.data.netTargetReturnRatePerAnnumRiskHigh = null;
				this.data.netTargetReturnRatePerAnnumRiskUnrated = null;
				this.data.targetReturnRateAddReferenceInterestRateWithoutTci = false;
			} else if (this.data.tciPreference === enumDef.alfaBuyPreference.Never) {
				this.data.netTargetReturnRatePerAnnumWithTci = null;
				this.data.targetReturnRateAddReferenceInterestRateWithTci = false;
			}
		},
		validate() {
			return validateForm(this, this.$validator);
		}
	},
	watch: {
		tciConfirmationStatus: {
			handler: function() {
				if (!enumDef.creditInsurancePolicyHolderStatus.canBuyTci(this.tciConfirmationStatus)) {
					this.data.tciPreference = enumDef.alfaBuyPreference.Never;
				}
			},
			immediate: true
		}
	}
};
</script>

<style lang="scss" scoped>
@import "alfa.scss";

.alfa_targets_limits {
	.md-checkbox,
	.md-radio {
		margin-top: 0.5rem;
		margin-bottom: 0.5rem;
	}

	.md-radio::v-deep {
		.md-radio-label {
			height: auto;
		}

		label:after {
			position: inherit;
		}
	}

	.roi_block {
		position: relative;

		.roi_block_tooltip {
			position: absolute;
			bottom: 5px;
			right: 5px;
		}
	}
}
</style>